import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { Progress } from '@heidi/design-system/progress';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { type ReactNode } from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';

import { clampCreditUsagePercent } from '../../../data/query/credit-allowance.utils';
import {
  CLINICIAN_MAX_COMPLIMENTARY_BANNER_BG_CLASS,
  CLINICIAN_MAX_COMPLIMENTARY_CARD_BORDER_CLASS,
  CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS,
  CLINICIAN_MAX_UPGRADE_BANNER_INK_CLASS,
} from '../clinician-max-upgrade-theme';
import { CreditUsageCoverageTooltip } from './credit-usage-coverage-tooltip';
import { creditUsagePopoverMessages } from './credit-usage-popover-messages';
import { CREDIT_USAGE_POPOVER_PRESETS } from './credit-usage-popover-presets';
import {
  formatCreditAllowanceCycleEndDate,
  formatCreditAllowanceResetCaption,
} from './format-credit-allowance-reset-label';
import { shouldShowComplimentaryUnlimitedLabel } from './should-show-complimentary-unlimited-label';
import type { CreditUsagePopoverContentProps } from './types';

/**
 * Dumb credit-usage popover panel. App wrappers supply usage data and upgrade wiring.
 */
export function CreditUsagePopoverContent({
  plan,
  usagePercent,
  cycleEnd,
  isOnClinicianMax = false,
  isComplimentaryUsage = false,
  isMaxUpgradeEnabled = false,
  trialRemaining,
  upgradeSeat = 'max',
  upgradeCtaLabel,
  onUpgradeClick,
  isUpgradePending = false,
  onExtendTrialClick,
  upgradeButtonRef,
  learnMoreHref,
  className,
  'data-testid': testId = 'credit-usage-popover-content',
}: CreditUsagePopoverContentProps) {
  const intl = useIntl();
  const { theme, planName } = CREDIT_USAGE_POPOVER_PRESETS[plan];
  const clampedUsagePercent = clampCreditUsagePercent(usagePercent);
  const resetCaption = formatCreditAllowanceResetCaption(intl, cycleEnd);
  const isTrialing = trialRemaining != null;
  const showUnlimitedLabel = shouldShowComplimentaryUnlimitedLabel(
    isComplimentaryUsage,
    clampedUsagePercent
  );
  const hideResetCaption = isComplimentaryUsage ? showUnlimitedLabel : isTrialing;
  const isStandardUpgrade = upgradeSeat === 'standard';
  let descriptionMessage: MessageDescriptor | null = null;
  const descriptionValues: Record<string, number> = { percent: clampedUsagePercent };
  if (isComplimentaryUsage || clampedUsagePercent >= 90) {
    descriptionMessage = null;
  } else if (isTrialing) {
    descriptionMessage = creditUsagePopoverMessages.freeUsageDescription;
  } else if (!isOnClinicianMax) {
    descriptionMessage = creditUsagePopoverMessages.consumedUsageDescription;
  }
  const showUpgradeCta =
    onUpgradeClick != null && (isStandardUpgrade || isMaxUpgradeEnabled);
  const TierIcon = plan === 'practiceMax' ? TierPracticeFilled : TierProFilled;
  const learnMore = (chunks: ReactNode) =>
    learnMoreHref ? (
      <Link
        variant="inline"
        href={learnMoreHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-text-primary decoration-text-primary/50 hover:text-text-primary hover:decoration-text-primary"
      >
        {chunks}
      </Link>
    ) : (
      chunks
    );
  const complimentaryBanner = isComplimentaryUsage
    ? {
        message: creditUsagePopoverMessages.complimentaryUntilBanner,
        values: {
          date: formatCreditAllowanceCycleEndDate(intl, cycleEnd),
          learnMore,
        },
      }
    : null;
  const complimentaryTone = {
    shellClass: CLINICIAN_MAX_COMPLIMENTARY_BANNER_BG_CLASS,
    inkClass: CLINICIAN_MAX_UPGRADE_BANNER_INK_CLASS,
    cardBorderClass: CLINICIAN_MAX_COMPLIMENTARY_CARD_BORDER_CLASS,
  };
  const thresholdBanner = isComplimentaryUsage
    ? null
    : clampedUsagePercent >= 100
      ? {
          message: creditUsagePopoverMessages.outOfUsageBanner,
          values: {},
          Icon: AlertTriangle,
          shellClass: 'bg-validation-destructive-muted',
          inkClass: 'text-validation-destructive',
          cardBorderClass: 'border-validation-destructive-border',
        }
      : clampedUsagePercent >= 90
        ? {
            message: creditUsagePopoverMessages.almostOutOfUsageBanner,
            values: {},
            Icon: AlertCircle,
            shellClass: 'bg-validation-warning-muted',
            inkClass: 'text-validation-warning',
            cardBorderClass: 'border-validation-warning-border',
          }
        : null;
  const statusBanner = thresholdBanner
    ? thresholdBanner
    : isTrialing
      ? {
          message:
            trialRemaining.unit === 'hours'
              ? creditUsagePopoverMessages.trialHoursRemainingDescription
              : creditUsagePopoverMessages.trialDaysRemainingDescription,
          values:
            trialRemaining.unit === 'hours'
              ? { hours: trialRemaining.value, plan, learnMore }
              : { days: trialRemaining.value, plan, learnMore },
          ...complimentaryTone,
        }
      : complimentaryBanner
        ? { ...complimentaryBanner, ...complimentaryTone }
        : null;

  const banner = statusBanner ? (
    <div
      className={cn(
        'flex gap-2 px-3 py-2',
        'Icon' in statusBanner ? 'items-center' : 'items-start'
      )}
      data-testid={`${testId}-status-banner`}
    >
      {'Icon' in statusBanner ? (
        <statusBanner.Icon
          className={cn('size-4 shrink-0', statusBanner.inkClass)}
          aria-hidden
        />
      ) : null}
      <TypographyCaptionBold className={statusBanner.inkClass}>
        <FormattedMessage {...statusBanner.message} values={statusBanner.values} />
      </TypographyCaptionBold>
    </div>
  ) : null;

  return (
    <div
      role="region"
      aria-label={intl.formatMessage(creditUsagePopoverMessages.title)}
      data-testid={testId}
      className={cn(
        'flex w-[289px] max-w-[calc(100vw-2rem)] flex-col',
        statusBanner?.shellClass,
        className
      )}
    >
      {banner}

      <div
        className={cn(
          'flex flex-col gap-4 p-3',
          statusBanner &&
            cn('rounded-lg border bg-surface-elevated', statusBanner.cardBorderClass)
        )}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <TypographyP2Bold className="text-text-primary">
              <FormattedMessage {...creditUsagePopoverMessages.title} />
            </TypographyP2Bold>
            <CreditUsageCoverageTooltip
              data-testid={`${testId}-coverage-tooltip-trigger`}
            />
          </div>

          {descriptionMessage ? (
            <TypographyCaption className="text-text-primary">
              <FormattedMessage {...descriptionMessage} values={descriptionValues} />
            </TypographyCaption>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <div>
            <Progress
              value={clampedUsagePercent}
              className={cn(
                'h-2 flex-1',
                'popoverProgressIndicatorClass' in theme
                  ? theme.popoverProgressIndicatorClass
                  : theme.progressIndicatorClass
              )}
              aria-label={intl.formatMessage(creditUsagePopoverMessages.title)}
            />
          </div>
          <div
            className={cn(
              'flex items-center gap-2',
              hideResetCaption ? 'justify-end' : 'justify-between'
            )}
          >
            {hideResetCaption ? null : (
              <TypographyCaption className="text-text-secondary">
                <FormattedMessage
                  {...creditUsagePopoverMessages.resetsOn}
                  values={resetCaption}
                />
              </TypographyCaption>
            )}
            <TypographyCaption className="shrink-0 text-text-secondary">
              {showUnlimitedLabel ? (
                <FormattedMessage {...creditUsagePopoverMessages.unlimitedUsage} />
              ) : (
                <FormattedMessage
                  {...creditUsagePopoverMessages.usagePercentUsed}
                  values={{ percent: clampedUsagePercent }}
                />
              )}
            </TypographyCaption>
          </div>
        </div>

        {showUpgradeCta || onExtendTrialClick ? (
          <div className="flex flex-col gap-2">
            {showUpgradeCta ? (
              <Button
                ref={upgradeButtonRef}
                type="button"
                variant={isStandardUpgrade ? 'secondary' : theme.buttonVariant}
                size="md"
                className={cn(
                  'w-full gap-x-1.5',
                  // Standard is not a tier, so both plans share the popover's blue fill.
                  isStandardUpgrade
                    ? CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS
                    : 'buttonClassName' in theme
                      ? theme.buttonClassName
                      : undefined
                )}
                onClick={onUpgradeClick}
                disabled={isUpgradePending}
                data-testid={`${testId}-upgrade`}
              >
                <TierIcon size={14} className="shrink-0" />
                {isComplimentaryUsage && !isTrialing ? (
                  <FormattedMessage {...creditUsagePopoverMessages.explorePlansCta} />
                ) : (
                  (upgradeCtaLabel ??
                  (isStandardUpgrade ? (
                    <FormattedMessage {...creditUsagePopoverMessages.upgrade} />
                  ) : (
                    <FormattedMessage
                      {...creditUsagePopoverMessages.upgradeCta}
                      values={{ planName: intl.formatMessage(planName) }}
                    />
                  )))
                )}
              </Button>
            ) : null}

            {onExtendTrialClick ? (
              <Button
                type="button"
                variant="outline"
                size="md"
                className="w-full"
                onClick={onExtendTrialClick}
                data-testid={`${testId}-extend-trial`}
              >
                <FormattedMessage {...creditUsagePopoverMessages.extendTrialCta} />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
